import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import useLivePrices, { formatPrice, formatChange } from '../../hooks/useLivePrices';

/**
 * LiveTicker — horizontal scrolling marquee of live prices from Polygon.io via /api/assets.
 * Drop-in replacement for any hardcoded ticker strip.
 *
 * Props:
 *  - symbols: optional string[] to filter (default = curated 8)
 *  - speed: animation duration in seconds (default 30)
 *  - dark: use dark theme (default true)
 */
const DEFAULT_SYMBOLS = ['EUR/USD', 'GBP/USD', 'USD/JPY', 'XAU/USD', 'BTC/USD', 'ETH/USD', 'AAPL', 'TSLA'];

const LiveTicker = ({ symbols = DEFAULT_SYMBOLS, speed = 30, dark = true }) => {
  const { prices, loading } = useLivePrices(symbols);
  const items = symbols
    .map((s) => prices[s])
    .filter(Boolean)
    .map((a) => ({
      s: a.symbol,
      p: formatPrice(a.bid, a.category, a.symbol),
      c: formatChange(a.change_24h * 100),
      up: (a.change_24h || 0) >= 0,
    }));

  // While loading or no data, render placeholders so layout doesn't shift
  const display = items.length ? items : symbols.map((s) => ({ s, p: '—', c: '—', up: true }));

  return (
    <div className={`${dark ? 'bg-ink-900 text-white' : 'bg-white text-ink-900 border-y border-flame-100'} py-3 overflow-hidden border-b-2 border-flame-500`} data-testid="live-ticker">
      <div className="flex gap-8 whitespace-nowrap" style={{ animation: `varomarquee ${speed}s linear infinite` }}>
        {[...display, ...display].map((t, i) => (
          <div key={i} className="inline-flex items-center gap-3 px-4">
            <span className="font-bold text-sm">{t.s}</span>
            <span className={`${dark ? 'text-white/70' : 'text-ink-600'} text-sm tabular-nums font-mono`}>{t.p}</span>
            <span className={`text-sm font-bold flex items-center gap-1 ${t.up ? 'text-emerald-400' : 'text-rose-400'}`}>
              {t.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {t.c}
            </span>
          </div>
        ))}
      </div>
      <style>{`@keyframes varomarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
      {loading && items.length === 0 && (
        <span className="sr-only">Loading live prices...</span>
      )}
    </div>
  );
};

export default LiveTicker;
